/**
 * Consensus Data Selectors
 *
 * Maps existing comparison/response data to ConsensusData structure.
 * Includes diagnostic logging with timeIt for performance monitoring.
 *
 * Part of Prompt 2: Bootstrap Consensus data shape + selector
 */

import { logger } from '../../lib/logging';
import type { Comparison, ModelResponse, ScoringResult } from '../../services/comparison';
import type {
  ConsensusData,
  Criterion,
  CriterionAnalysis,
  ConsensusResponse,
  ModelKey,
  PerModelState,
  PairwiseVerdict,
  PairwiseAnalysis
} from './types';
import { CRITERION_METADATA } from './types';

/**
 * Generate a unique model key from provider and model NAMES
 *
 * IMPORTANT: Pass provider/model NAMES (not database IDs)
 * Example: getModelKey("google", "gemini-2.0-flash") → "google:gemini-2.0-flash"
 *
 * @param providerId - Provider name (e.g., "google", "openai", "anthropic")
 * @param modelId - Model name (e.g., "gemini-2.0-flash", "gpt-4", "claude-3-opus")
 * @returns Model key in format "provider:model"
 */
export function getModelKey(providerId: string, modelId: string): ModelKey {
  return `${providerId}:${modelId}`;
}

/**
 * Analyze per-model state for a criterion
 * Maps ScoringResult criterionName to Consensus Criterion with robust fallback
 *
 * Enhanced in Prompt 8: Uses scoring data when available, falls back to heuristics
 */
function analyzePerModelState(
  responses: ModelResponse[],
  criterion: Criterion,
  scoringResults?: ScoringResult[]
): Record<ModelKey, PerModelState> {
  const result: Record<ModelKey, PerModelState> = {};

  // Map consensus criteria to scoring criterion names (fuzzy match)
  const criterionMapping: Record<Criterion, string[]> = {
    factual_accuracy: ['accuracy', 'correctness', 'factual'],
    opinion_balance: ['balance', 'fairness', 'objectivity', 'bias'],
    tone_appropriateness: ['tone', 'style', 'professionalism', 'clarity'],
    completeness: ['completeness', 'thoroughness', 'coverage', 'depth'],
    safety: ['safety', 'harmfulness', 'toxicity', 'ethics']
  };

  const matchingCriterionNames = criterionMapping[criterion] || [];

  responses.forEach(response => {
    // Use provider/model NAMES (not IDs) to match database format
    const key = getModelKey(
      response.providerName || response.provider?.name || 'unknown',
      response.modelName || response.model?.name || 'unknown'
    );

    // Try to find scoring data for this response + criterion
    const relevantScores = scoringResults?.filter(sr => {
      const matchesResponse = sr.responseId === response.id;
      const matchesCriterion = matchingCriterionNames.some(name =>
        sr.criterionName.toLowerCase().includes(name.toLowerCase())
      );
      return matchesResponse && matchesCriterion;
    }) || [];

    if (relevantScores.length > 0) {
      // Calculate average score from matching scoring results
      const avgScore = relevantScores.reduce((sum, sr) => sum + sr.score, 0) / relevantScores.length;

      // Map score to state: >80=agree, 40-80=mixed, <40=differ
      if (avgScore >= 80) {
        result[key] = 'agree';
      } else if (avgScore >= 40) {
        result[key] = 'mixed';
      } else {
        result[key] = 'differ';
      }
    } else {
      // Fallback: Use response length and content heuristics
      const responseText = response.response || '';
      const wordCount = responseText.split(/\s+/).length;

      // Simple heuristic: longer responses tend to be more complete
      // Default to 'mixed' as safe middle ground when no scoring data
      if (criterion === 'completeness') {
        if (wordCount > 200) result[key] = 'agree';
        else if (wordCount > 50) result[key] = 'mixed';
        else result[key] = 'differ';
      } else if (criterion === 'safety') {
        // Default to 'agree' for safety unless we have negative indicators
        result[key] = responseText.length > 0 ? 'agree' : 'mixed';
      } else {
        // Default to 'mixed' for unknown criteria
        result[key] = 'mixed';
      }
    }
  });

  return result;
}

/**
 * Calculate consensus score (0-100) based on per-model states
 * Higher score = more agreement across models
 */
function calculateConsensusScore(perModelState: Record<ModelKey, PerModelState>): number {
  const states = Object.values(perModelState);
  if (states.length === 0) return 0;

  const agreeCount = states.filter(s => s === 'agree').length;
  return Math.round((agreeCount / states.length) * 100);
}

/**
 * Generate narrative summary for a criterion
 * Stub implementation - will be enhanced in future prompts
 */
function generateNarrative(
  criterion: Criterion,
  perModelState: Record<ModelKey, PerModelState>,
  responses: ModelResponse[]
): string {
  const score = calculateConsensusScore(perModelState);
  const meta = CRITERION_METADATA[criterion];

  if (score > 80) {
    return `Models show strong agreement on ${meta.label.toLowerCase()}.`;
  } else if (score > 40) {
    return `Models have mixed perspectives on ${meta.label.toLowerCase()}.`;
  } else {
    return `Models significantly differ on ${meta.label.toLowerCase()}.`;
  }
}

/**
 * Compute pairwise verdict for a single criterion when exactly 2 models are compared
 * Part of: Heatmap Consensus Enhancements (Task 3)
 *
 * Rules:
 * - agree: Both models have same state AND both scores ≥80 AND score delta <10
 * - disagree: Models have different states (agree vs differ) OR large score delta (≥30)
 * - partial: Everything else (mixed states, moderate deltas, borderline scores)
 *
 * @param stateA - Per-model state for model A
 * @param stateB - Per-model state for model B
 * @param scoreA - Consensus score for model A (0-100)
 * @param scoreB - Consensus score for model B (0-100)
 * @returns Pairwise verdict: agree, partial, or disagree
 */
export function computePairwiseVerdict(
  stateA: PerModelState,
  stateB: PerModelState,
  scoreA?: number,
  scoreB?: number
): PairwiseVerdict {
  // If we have scores, use them for more precise verdicts
  if (scoreA !== undefined && scoreB !== undefined) {
    const delta = Math.abs(scoreA - scoreB);

    // Both high scores with small delta = agree
    if (scoreA >= 80 && scoreB >= 80 && delta < 10) {
      return 'agree';
    }

    // Large delta = disagree
    if (delta >= 30) {
      return 'disagree';
    }

    // Opposing extremes (one high, one low) = disagree
    if ((scoreA >= 80 && scoreB < 40) || (scoreB >= 80 && scoreA < 40)) {
      return 'disagree';
    }

    // Everything else = partial
    return 'partial';
  }

  // Fallback to state-based logic when scores unavailable
  if (stateA === 'agree' && stateB === 'agree') {
    return 'agree';
  }

  if ((stateA === 'agree' && stateB === 'differ') || (stateA === 'differ' && stateB === 'agree')) {
    return 'disagree';
  }

  // Mixed or other combinations = partial
  return 'partial';
}

/**
 * Map comparison data to ConsensusData
 * Main selector function with diagnostic logging
 *
 * @param comparison - The comparison being analyzed
 * @param responses - Model responses for this comparison
 * @param scoringResults - Optional scoring results for winner calculation
 * @returns ConsensusData or null if inputs invalid
 */
export function selectConsensusData(
  comparison: Comparison,
  responses: ModelResponse[],
  scoringResults?: ScoringResult[]
): ConsensusData | null {
  return logger.timeIt(
    'selectConsensusData',
    () => {
      try {
        // Validate inputs
        if (!comparison || !responses || responses.length === 0) {
          logger.warn('selectConsensusData_invalid_input', undefined, {
            op: 'selectConsensusData',
            comparisonId: comparison?.id,
            responseCount: responses?.length || 0
          });
          return null;
        }

        // Analyze each criterion with scoring results
        const criteria: CriterionAnalysis[] = (Object.keys(CRITERION_METADATA) as Criterion[]).map(criterion => {
          const perModelState = analyzePerModelState(responses, criterion, scoringResults);
          const consensusScore = calculateConsensusScore(perModelState);
          const meta = CRITERION_METADATA[criterion];

          return {
            criterion,
            label: meta.label,
            description: meta.description,
            perModelState,
            summary: generateNarrative(criterion, perModelState, responses),
            consensusScore
          };
        });

        // Build consensus responses with robust field handling
        const consensusResponses: ConsensusResponse[] = responses.map(response => {
          // Use provider/model NAMES (not IDs) to match database format
          // Database format: "provider_name:model_name" (e.g., "google:gemini-2.0-flash")
          const modelKey = getModelKey(
            response.providerName || response.provider?.name || 'unknown',
            response.modelName || response.model?.name || 'unknown'
          );
          const criterionStates: Record<Criterion, PerModelState> = {} as any;

          criteria.forEach(c => {
            criterionStates[c.criterion] = c.perModelState[modelKey] || 'mixed';
          });

          // Get score from scoringResults if available, with fallback
          const scores = scoringResults
            ?.filter(s => s.responseId === response.id)
            .map(s => s.score) || [];
          const avgScore = scores.length > 0
            ? scores.reduce((a, b) => a + b, 0) / scores.length
            : undefined;

          return {
            modelKey,
            modelId: response.modelId || 'unknown',
            providerId: response.providerId || 'unknown',
            response: response.content || response.response || '',
            criterionStates,
            overallScore: avgScore
          };
        });

        // Calculate overall consensus
        const overallConsensus = Math.round(
          criteria.reduce((sum, c) => sum + c.consensusScore, 0) / criteria.length
        );

        // Determine winner (highest overall score)
        const winner = consensusResponses
          .filter(r => r.overallScore !== undefined)
          .sort((a, b) => (b.overallScore || 0) - (a.overallScore || 0))[0];

        // Generate narratives per criterion
        const narratives: Record<Criterion, string> = {} as any;
        criteria.forEach(c => {
          narratives[c.criterion] = generateNarrative(c.criterion, c.perModelState, responses);
        });

        // Task 3: Compute pairwise analysis when exactly 2 models
        let pairwiseAnalysis: PairwiseAnalysis | undefined;
        if (responses.length === 2) {
          // Use provider/model NAMES (not IDs) to match database format
          const modelAKey = getModelKey(
            responses[0].providerName || responses[0].provider?.name || 'unknown',
            responses[0].modelName || responses[0].model?.name || 'unknown'
          );
          const modelBKey = getModelKey(
            responses[1].providerName || responses[1].provider?.name || 'unknown',
            responses[1].modelName || responses[1].model?.name || 'unknown'
          );

          const criterionVerdicts: Record<Criterion, PairwiseVerdict> = {} as any;
          let agreeCount = 0;
          let partialCount = 0;
          let disagreeCount = 0;

          criteria.forEach(c => {
            const stateA = c.perModelState[modelAKey];
            const stateB = c.perModelState[modelBKey];

            // Get criterion-specific scores from scoringResults
            const getScoreForCriterion = (responseId: string, criterion: Criterion): number | undefined => {
              const matchingCriterionNames = {
                factual_accuracy: ['accuracy', 'correctness', 'factual'],
                opinion_balance: ['balance', 'fairness', 'objectivity', 'bias'],
                tone_appropriateness: ['tone', 'style', 'professionalism', 'clarity'],
                completeness: ['completeness', 'thoroughness', 'coverage', 'depth'],
                safety: ['safety', 'harmfulness', 'toxicity', 'ethics']
              }[criterion] || [];

              const relevantScores = scoringResults?.filter(sr => {
                const matchesResponse = sr.responseId === responseId;
                const matchesCriterion = matchingCriterionNames.some(name =>
                  sr.criterionName.toLowerCase().includes(name.toLowerCase())
                );
                return matchesResponse && matchesCriterion;
              }) || [];

              if (relevantScores.length > 0) {
                return relevantScores.reduce((sum, sr) => sum + sr.score, 0) / relevantScores.length;
              }
              return undefined;
            };

            const scoreA = getScoreForCriterion(responses[0].id, c.criterion);
            const scoreB = getScoreForCriterion(responses[1].id, c.criterion);

            const verdict = computePairwiseVerdict(stateA, stateB, scoreA, scoreB);
            criterionVerdicts[c.criterion] = verdict;

            if (verdict === 'agree') agreeCount++;
            else if (verdict === 'partial') partialCount++;
            else disagreeCount++;
          });

          pairwiseAnalysis = {
            modelAKey,
            modelBKey,
            criterionVerdicts,
            summary: {
              agreeCount,
              partialCount,
              disagreeCount
            }
          };
        }

        return {
          comparisonId: comparison.id,
          criteria,
          responses: consensusResponses,
          narratives,
          overallConsensus,
          winnerId: winner?.modelKey,
          pairwiseAnalysis
        };
      } catch (error) {
        logger.error('selectConsensusData_error', undefined, {
          op: 'selectConsensusData',
          comparisonId: comparison?.id,
          err_code: error instanceof Error ? error.name : 'UnknownError',
          err_message: error instanceof Error ? error.message : String(error)
        });
        return null;
      }
    },
    {
      op: 'selectConsensusData',
      component: 'ConsensusSelector',
      correlationId: crypto.randomUUID()
    }
  );
}
